import React, { useState, useRef, useEffect, useCallback } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import Icon from "../icons";
import styles from "../../Style/Components/date-picker.module.scss";

// ─── Helpers ────────────────────────────────────────────────────────────────

function parseDdMmYyyyToLocalDate(str) {
  if (typeof str !== "string" || !/^\d{2}-\d{2}-\d{4}$/.test(str)) return null;
  const [day, month, year] = str.split("-").map(Number);
  const d = new Date(year, month - 1, day);
  return isNaN(d.getTime()) ? null : d;
}

function toCalendarDate(value) {
  if (!value) return null;
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  if (typeof value === "string") return parseDdMmYyyyToLocalDate(value);
  return null;
}

function normalizePropToRange(value) {
  if (!value) return [null, null];
  if (Array.isArray(value)) {
    return [toCalendarDate(value[0]) ?? null, toCalendarDate(value[1]) ?? null];
  }
  if (typeof value === "object") {
    return [
      toCalendarDate(value.from_date) ?? null,
      toCalendarDate(value.till_date) ?? null,
    ];
  }
  return [null, null];
}

// Stable key so the value-sync effect doesn't fire on every render
function getRangeKey(value) {
  if (!value) return "|";
  if (Array.isArray(value)) return `${value[0] ?? ""}|${value[1] ?? ""}`;
  return `${value?.from_date ?? ""}|${value?.till_date ?? ""}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function CustomDatePicker({
  value = [null, null],
  onChange,
  placeholder = "Select date range",
  disabled = false,
  minDate,
  maxDate,
  calenderPosition = "",
  error,
  fillHeight = false,
}) {
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(() =>
    normalizePropToRange(value)
  );
  // FIX 1: Controlled activeStartDate so month navigation actually works
  const [activeStartDate, setActiveStartDate] = useState(() => {
    const [start] = normalizePropToRange(value);
    return start ?? new Date();
  });

  const wrapperRef = useRef(null);
  const valueKey = getRangeKey(value);

  // Sync internal state when the parent value prop changes
  useEffect(() => {
    const normalized = normalizePropToRange(value);
    setInternalValue(normalized);
    // Also update the visible month to the start date when value changes externally
    if (normalized[0]) setActiveStartDate(normalized[0]);
  }, [valueKey]);

  // FIX 2: Click-outside uses the ref id safely; avoid id collisions by using
  //         the ref directly instead of a hardcoded string id
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    if (open) {
      window.addEventListener("mousedown", handleClickOutside);
    }
    return () => window.removeEventListener("mousedown", handleClickOutside);
  }, [open]); // re-bind only when open state changes

  // FIX 3: Scroll into view only after the popup has mounted (requestAnimationFrame)
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      const popup = wrapperRef.current?.querySelector("#cdp-popup-wrapper");
      popup?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // ── Formatters ────────────────────────────────────────────────────────────

  const formatToPayload = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  };

  const formatDisplay = (date) => {
    const d = toCalendarDate(date);
    if (!d) return null;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatWeekDay = useCallback(
    (_locale, date) => ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"][date.getDay()],
    []
  );

  // ── Handlers ──────────────────────────────────────────────────────────────

  // FIX 4: react-calendar in selectRange mode returns either a [Date, Date]
  //        tuple OR a single Date (when allowPartialRange fires mid-pick).
  //        Guard both cases so we never crash on .getTime().
  const handleSelect = (calendarValue) => {
    // Mid-pick: only start selected
    if (!Array.isArray(calendarValue)) {
      setInternalValue([calendarValue, null]);
      return;
    }

    const [start, end] = calendarValue;
    setInternalValue([start, end ?? null]);

    if (start && end) {
      onChange?.({
        from_date: formatToPayload(start),
        till_date: formatToPayload(end),
      });
      setOpen(false);
    }
  };

  // FIX 1 (continued): persist the month the user navigated to
  const handleActiveStartDateChange = ({ activeStartDate: newDate }) => {
    if (newDate) setActiveStartDate(newDate);
  };

  const handleClear = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setInternalValue([null, null]);
    setActiveStartDate(new Date());
    onChange?.({});
    setOpen(false);
  };

  const handleTriggerClick = () => {
    if (disabled) return;
    setOpen((prev) => {
      if (!prev) {
        // When opening, jump to the month of the selected start date (if any)
        const start = internalValue[0];
        setActiveStartDate(start instanceof Date ? start : new Date());
      }
      return !prev;
    });
  };

  // ── Derived display ───────────────────────────────────────────────────────

  const [start, end] = internalValue;
  const hasRange = start && end;
  const hasStart = start && !end;

  const displayLabel = hasRange
    ? `${formatDisplay(start)} – ${formatDisplay(end)}`
    : hasStart
    ? `${formatDisplay(start)} – End date`
    : null;

  // FIX 5: Pass a valid [Date|null, Date|null] tuple to react-calendar.
  //        Passing [null, null] directly can cause an internal warning/crash
  //        in some versions; fall back to null when no selection.
  const calendarValue = hasRange
    ? [start, end]
    : hasStart
    ? [start, null]
    : null;

  // FIX 6: Clamp maxDate so it never ends up before minDate (would crash calendar)
  const safeMaxDate = (() => {
    const base = maxDate || new Date();
    if (minDate && base < minDate) return minDate;
    return base;
  })();

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      id="cdp-date-picker-wrapper"
      className={`${styles.cdp_wrapper} position-relative d-flex`}
      ref={wrapperRef}
    >
      {/* Trigger Button */}
      <button
        type="button"
        id="cdp-trigger"
        className={`${styles.cdp_input} ${disabled ? styles.disabled : ""} ${
          open ? styles.active : ""
        } ${error ? styles.error : ""} ${
          fillHeight ? styles.fill_height : ""
        } d-flex align-items-center justify-content-between`}
        onClick={handleTriggerClick}
        // FIX 7: Explicitly mark as not submitting so it doesn't accidentally
        //        submit a parent <form> in some edge cases
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
      >
        <div className="d-flex align-items-center gap-2">
          <span className={displayLabel ? styles.has_value : styles.placeholder}>
            {displayLabel || placeholder}
          </span>
        </div>

        <div className="d-flex column-gap-2 align-items-center">
          <Icon icon={open ? "chevron-up" : "chevron-down"} size="18px" />
          {hasRange && (
            <Icon
              onClick={handleClear}
              icon="close"
              className="text-danger"
              size="14px"
            />
          )}
        </div>
      </button>

      {/* Error */}
      {error && (
        <p className={`${styles.cdp_error} e-font-12 e-primary-font-poppins mb-0`}>
          {error}
        </p>
      )}

      {/* Calendar Popup */}
      {open && (
        <div
          id="cdp-popup-wrapper"
          role="dialog"
          aria-label="Date range picker"
          className={`${styles.cdp_popup} ${calenderPosition} position-absolute bg-white z-3`}
        >
          <Calendar
            selectRange
            allowPartialRange
            value={calendarValue}           // FIX 5
            activeStartDate={activeStartDate} // FIX 1
            onActiveStartDateChange={handleActiveStartDateChange} // FIX 1
            minDate={minDate}
            maxDate={safeMaxDate}           // FIX 6
            nextLabel={<Icon icon="arrow-right" size="16px" />}
            next2Label={null}
            prevLabel={<Icon icon="arrow-left" size="16px" />}
            prev2Label={null}
            showNeighboringMonth={false}
            locale="en-IN"
            formatShortWeekday={formatWeekDay}
            onChange={handleSelect}         // FIX 4
            className={`border-0 e-primary-font-poppins ${styles.cdp_calendar}`}
          />
        </div>
      )}
    </div>
  );
}